import { Box } from "@material-ui/core";
import DialogConfirmError from "@saleor/components/DialogConfirmError";
import DialogConfirm from "@saleor/createOrChangeNotRefContract/components/DialogConfirm";
import useNotifier from "@saleor/hooks/useNotifier";
import { FormikProps } from "formik";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";

import CPResultModal from "../CPResultModal";
import OrderProcessingFailed from "../OrderProcessingFailed/OrderProcessingFailed";

export interface DialogPopUpProps {
  open: boolean;
  errors: any;
  formikCPItemDetails: FormikProps<any>;
  formikItemDetails: FormikProps<any>;
  currentAction: any;
  onConfirm: () => void;
  onFetchOrderData: () => void;
}

const DialogPopUp: React.FC<DialogPopUpProps> = ({
  open,
  errors,
  formikCPItemDetails,
  formikItemDetails,
  currentAction,
  onConfirm,
  onFetchOrderData
}) => {
  const notify = useNotifier();
  const [dialogError, setDialogError] = useState(false);
  const [errorSource, setErrorSource] = useState("");
  const [errorOrders, setErrorOrders] = useState([]);
  const [errorItems, setErrorItems] = useState([]);
  const [openCPResultModal, setOpenCPResultModal] = useState(false);
  const [openOrderProcessingFailed, setOpenOrderProcessingFailed] = useState(
    false
  );
  const [openCPConfirmModal, setOpenCPConfirmModal] = useState(false);

  const showErrorNotification = (OrderErrors, ItemErrors, source) => {
    notify({
      status: "error",
      title: "Something went wrong",
      text: (
        <>
          <Box>
            <b>{`Error  ${source}`}</b>
          </Box>
          <Box>{`${OrderErrors}  ${ItemErrors}`}</Box>
        </>
      )
    });
  };

  const processSapErrors = () => {
    const sapErrorOrders = errors?.sapOrderMessages?.map(
      ({ errorCode, errorMessage }) => `${errorCode || ""} - ${errorMessage}`
    );

    if (currentAction !== "split") {
      const sapErrorItems = errors?.sapItemMessages?.map(
        ({ itemNo, errorMessage, errorCode }) => ({
          itemNo,
          itemCode: `${errorCode || ""}`,
          itemStatus: errorMessage
        })
      );
      setDialogError(true);
      setErrorOrders(sapErrorOrders);
      setErrorItems(sapErrorItems);
      setErrorSource("SAP");
    } else {
      const sapErrorItems = errors?.sapItemMessages?.map(
        ({ itemNo, errorMessage, errorCode }) =>
          `${itemNo || ""} ${errorCode || ""} - ${errorMessage}`
      );
      showErrorNotification(sapErrorOrders, sapErrorItems, "SAP");
    }
  };

  const processCpErrors = () => {
    const cpErrorOrders = errors?.cpErrorMessages?.map(
      ({ errorCode, errorMessage }) => `${errorCode || ""} - ${errorMessage}`
    );

    if (currentAction !== "split") {
      const cpErrorItems = errors?.cpErrorMessages?.map(
        ({ itemNo, errorCode, errorMessage }) => ({
          itemNo,
          itemCode: `${errorCode || ""}`,
          itemStatus: errorMessage
        })
      );
      setErrorItems(cpErrorItems);
      setErrorOrders(cpErrorOrders);
      setErrorSource("CP");
      setDialogError(true);
    } else {
      const cpErrorItems = errors?.cpErrorMessages?.map(
        ({ itemNo, errorMessage, errorCode }) =>
          `${itemNo || ""} ${errorCode || ""} - ${errorMessage}`
      );
      showErrorNotification(cpErrorOrders, cpErrorItems, "CP");
    }
  };

  const processingFailedError = () => {
    if (errors?.errorOutDated) {
      setErrorSource(errors?.errorOutDated.message);
    }
    setOpenOrderProcessingFailed(true);
  };

  useEffect(() => {
    if (open) {
      if (
        (errors?.sapOrderMessages?.length > 0 ||
          errors?.sapItemMessages?.length > 0) &&
        !errors?.errorOutDated
      ) {
        processSapErrors();
      } else if (errors?.cpErrorMessages?.length > 0) {
        processCpErrors();
      } else if (errors?.cpItemMessages?.length > 0) {
        setOpenCPResultModal(true);
      } else if (currentAction !== "split" || errors?.errorOutDated) {
        processingFailedError();
      }
    }
  }, [errors]);

  const handleAcceptConfirmDate = () => {
    formikCPItemDetails.setValues(prev => ({
      lines: prev.lines.map(line => ({
        ...line,
        requestDate: moment(line?.confirmDate, [
          "DD/MM/YYYY",
          "YYYY/MM/DD",
          "DD-MM-YYYY"
        ]).format("YYYY-MM-DD")
      }))
    }));
    formikItemDetails.setValues(prev => ({
      lines: prev.lines.map(line => {
        const itemNo =
          currentAction === "split" && !line?.splitOriginal
            ? line?.splitItemNo
            : line?.itemNo;
        const cpItem = formikCPItemDetails.values?.lines.find(
          item => item.itemNo === itemNo
        );

        if (cpItem) {
          return {
            ...line,
            requestDate: moment(cpItem?.confirmDate, [
              "DD/MM/YYYY",
              "YYYY/MM/DD",
              "DD-MM-YYYY"
            ]).format("YYYY-MM-DD"),
            plant: cpItem?.plant
          };
        } else {
          return line;
        }
      })
    }));
    setOpenCPResultModal(false);
    setOpenCPConfirmModal(true);
  };

  const handleNotAcceptConfirmDate = () => {
    formikCPItemDetails.setValues(prev => ({
      lines: prev.lines.map(line => {
        if (line?.parentItemNo) {
          return {
            ...line,
            requestDate: moment(
              formikCPItemDetails.values?.lines.find(
                item => item.itemNo === line?.parentItemNo
              )?.requestDate,
              ["DD/MM/YYYY", "YYYY/MM/DD", "DD-MM-YYYY"]
            ).format("YYYY-MM-DD")
          };
        } else {
          return line;
        }
      })
    }));
    formikItemDetails.setValues(prev => ({
      lines: prev.lines.map(line => {
        const itemNo =
          currentAction === "split" && !line?.splitOriginal
            ? line?.splitItemNo
            : line?.itemNo;
        const cpItem = formikCPItemDetails.values?.lines.find(
          item => item.itemNo === itemNo
        );
        if (cpItem) {
          return {
            ...line,
            requestDate: cpItem?.requestDate,
            plant: cpItem?.plant
          };
        } else {
          return line;
        }
      })
    }));
    setOpenCPConfirmModal(true);
    setOpenCPResultModal(false);
  };
  const handleOnConfirm = () => {
    setOpenCPConfirmModal(false);
    onConfirm();
  };

  const handleConfirmProcessingFailed = () => {
    if (errors?.errorOutDated) {
      onFetchOrderData();
    }
    setOpenOrderProcessingFailed(false);
  };

  return (
    <>
      <OrderProcessingFailed
        onConfirm={handleConfirmProcessingFailed}
        onCancel={() => setOpenOrderProcessingFailed(false)}
        errorSource={errorSource}
        open={openOrderProcessingFailed}
      />
      <CPResultModal
        visible={openCPResultModal}
        formikCPItemDetails={formikCPItemDetails}
        formikItemDetails={formikItemDetails}
        onAcceptConfirmDate={handleAcceptConfirmDate}
        onNotAcceptConfirmDate={handleNotAcceptConfirmDate}
      />
      <DialogConfirmError
        open={dialogError}
        onConfirm={() => setDialogError(false)}
        onCancel={() => setDialogError(false)}
        errorSource={errorSource}
        errorOrders={errorOrders}
        errorItems={errorItems}
      />
      <DialogConfirm
        isOpen={openCPConfirmModal}
        title={"ระบบ eOrdering"}
        contentText={"ได้รับข้อมูลของคุณเรียบร้อยแล้ว"}
        buttonText={"ดำเนินการต่อ"}
        onClose={handleOnConfirm}
      />
    </>
  );
};

export default DialogPopUp;
